import { useEffect, useState } from "react"
import type { CarriageLabelRow } from "./carriageLabelsTypes"
import { CarriageLabel } from "./templates/CarriageLabel"
import { buildCarriageLabelsPdf } from "./carriageLabelPdf"
import { downloadPdf } from "./pdfBuilder"

// "Create custom label" — a free-type A5 carriage label for anything that isn't
// a cached MERAC group booking (a late charter, a private hire, a replacement for
// a "Unknown to Unknown" leg). Every field is plain text so it can say whatever
// the platform needs; nothing here touches the cache or the API.
//
// Nested inside the A La Carte modal, so it sits on its own fixed overlay above
// it and swallows Escape before the parent modal sees it.

export interface CustomCarriageLabelDialogProps {
  open: boolean
  onClose: () => void
}

interface FormState {
  banner: string
  account: string
  route: string
  time: string
  date: string
  qty: string
  booking: string
  count: string
}

function longToday(): string {
  return new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
}

function blankForm(): FormState {
  return { banner: "RESERVED", account: "", route: "", time: "", date: longToday(), qty: "", booking: "", count: "1" }
}

/** One row per label; "Label X of Y" only appears when more than one is asked for. */
function rowsFrom(f: FormState): CarriageLabelRow[] {
  const count = Math.max(1, Math.min(200, Math.trunc(Number(f.count) || 1)))
  const qtyNum = Number(f.qty)
  const qty = f.qty.trim() !== "" && Number.isFinite(qtyNum) ? qtyNum : null
  const booking = f.booking.trim() !== "" ? f.booking.trim() : null
  return Array.from({ length: count }, (_, i) => ({
    JourneyDate: null,
    JourneyDayDate: f.date.trim(),
    OutFrom: f.route.trim(),
    TimeOut: f.time.trim(),
    Account: f.account.trim(),
    Qty: qty,
    LabelNo: count > 1 ? i + 1 : null,
    OfLabels: count > 1 ? count : null,
    Booking: booking,
    BannerText: f.banner.trim(),
  }))
}

function slug(s: string): string {
  return (s || "label").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "label"
}

export function CustomCarriageLabelDialog({ open, onClose }: CustomCarriageLabelDialogProps) {
  const [f, setF] = useState<FormState>(blankForm)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null)

  useEffect(() => { if (open) { setF(blankForm()); setBusy(false); setError(null); setProgress(null) } }, [open])

  // Capture-phase so Escape closes this dialog only, not the A La Carte modal
  // behind it (which listens on document in the bubble phase).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose() }
    }
    document.addEventListener("keydown", onKey, true)
    return () => document.removeEventListener("keydown", onKey, true)
  }, [open, onClose])

  if (!open) return null

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }))

  const rows = rowsFrom(f)
  const preview = rows[0]!
  const canBuild = f.account.trim() !== "" || f.route.trim() !== ""

  const onCreate = async () => {
    if (!canBuild) { setError("Give the label a name or a route first."); return }
    setBusy(true); setError(null); setProgress({ current: 0, total: rows.length })
    try {
      const res = await buildCarriageLabelsPdf({ rows, onProgress: (current, total) => setProgress({ current, total }) })
      downloadPdf(res.blob, `carriage-label-${slug(f.account || f.route)}.pdf`)
      if (res.failed.length > 0) setError(`${res.failed.length} label(s) failed to render.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setBusy(false); setProgress(null) }
  }

  return (
    <div
      className="cl-custom-overlay"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="cl-custom" role="dialog" aria-modal="true" aria-labelledby="cl-custom-title">
        <div className="cl-custom-head">
          <h3 id="cl-custom-title">Create custom label</h3>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="cl-custom-body">
          <div className="cl-custom-form">
            <label className="alacarte-field"><span>Banner</span>
              <input type="text" value={f.banner} onChange={set("banner")} placeholder="RESERVED" /></label>
            <label className="alacarte-field"><span>Group or name</span>
              <input type="text" value={f.account} onChange={set("account")} placeholder="Leger Shearings" /></label>
            <label className="alacarte-field"><span>Route</span>
              <input type="text" value={f.route} onChange={set("route")} placeholder="Pickering to Whitby" /></label>
            <label className="alacarte-field"><span>Departure time</span>
              <input type="text" value={f.time} onChange={set("time")} placeholder="10:55" /></label>
            <label className="alacarte-field"><span>Date</span>
              <input type="text" value={f.date} onChange={set("date")} placeholder="Tuesday, 28 July 2026" /></label>
            <label className="alacarte-field"><span>Party size</span>
              <input type="text" value={f.qty} onChange={set("qty")} placeholder="48" inputMode="numeric" /></label>
            <label className="alacarte-field"><span>Booking reference</span>
              <input type="text" value={f.booking} onChange={set("booking")} placeholder="273699" /></label>
            <label className="alacarte-field"><span>How many labels</span>
              <input type="number" min={1} max={200} value={f.count} onChange={set("count")} /></label>
          </div>

          <div className="cl-custom-preview">
            <div className="cl-custom-preview-label">Preview</div>
            <div className="cl-custom-preview-sheet">
              <CarriageLabel row={preview} style={{ width: "100%", height: "auto" }} />
            </div>
            <p className="cl-custom-preview-note">
              A5 landscape, 210 &times; 148&nbsp;mm. Leave a field blank to drop that line.
              {rows.length > 1 ? ` Numbered “Label 1 of ${rows.length}” through “${rows.length} of ${rows.length}”.` : " A single label is printed without a “Label X of Y” line."}
            </p>
          </div>
        </div>

        <footer className="alacarte-footer">
          <div className="alacarte-footer-summary">
            {error
              ? <span className="alacarte-footer-error">{error}</span>
              : progress
                ? <span>Rendering {progress.current} / {progress.total}&hellip;</span>
                : <span>{rows.length} label{rows.length === 1 ? "" : "s"} ready</span>}
          </div>
          <div className="alacarte-footer-actions">
            <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="btn btn--primary" onClick={() => void onCreate()} disabled={busy || !canBuild}>
              {busy ? "Creating…" : "Create PDF"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
